import type { TaleDef } from '../types';

/**
 * Tales are flavour events rolled on a timer. Grants are expressed in seconds
 * of current production so they stay relevant at every scale, and unfavourable
 * tales are deliberately mild — a tale never loses a run.
 */
export const TALES: TaleDef[] = [
  // --- Age I ---------------------------------------------------------------
  {
    id: 'berryThicket',
    title: 'A Thicket in Fruit',
    text: 'A child comes back stained to the elbows and will not say where from. The tribe follows the stains.',
    minAge: 0,
    weight: 10,
    favourable: true,
    effects: [{ kind: 'grantSeconds', resource: 'forage', seconds: 180 }],
  },
  {
    id: 'flintSeam',
    title: 'The Broken Bank',
    text: 'Rain has cut into a slope and left good stone lying in the open, already half-knapped by the fall.',
    minAge: 0,
    weight: 10,
    favourable: true,
    effects: [{ kind: 'grantSeconds', resource: 'flint', seconds: 180 }],
  },
  {
    id: 'dryWind',
    title: 'A Dry Wind',
    text: 'Three days of it. Everything the tribe has hung out has come in light and crackling.',
    minAge: 0,
    weight: 9,
    favourable: true,
    effects: [{ kind: 'grantSeconds', resource: 'tinder', seconds: 200 }],
  },
  {
    id: 'wanderersJoin',
    title: 'Strangers at the Edge',
    text: 'Two of them, thin, holding empty hands out where they can be seen. By dusk they are sitting at the fire.',
    minAge: 0,
    weight: 6,
    favourable: true,
    minPopulation: 4,
    effects: [{ kind: 'population', amount: 2 }],
  },
  {
    id: 'goodSeason',
    title: 'A Kind Season',
    text: 'Nothing in particular happens. Nothing goes wrong. Everyone works, and the work goes well.',
    minAge: 0,
    weight: 8,
    favourable: true,
    effects: [{ kind: 'buff', amount: 0.3, durationSeconds: 180 }],
  },
  {
    id: 'coldSnap',
    title: 'A Cold Snap',
    text: 'The ground goes hard overnight. Nothing can be dug, and the digging was not finished.',
    minAge: 0,
    weight: 7,
    favourable: false,
    effects: [{ kind: 'buff', amount: -0.15, durationSeconds: 150 }],
  },
  {
    id: 'anOldOneDies',
    title: 'One of the Old Ones',
    text: 'She goes in her sleep, which is rare, and the tribe treats it as luck rather than loss. What she knew, she had already said twice.',
    minAge: 0,
    weight: 5,
    favourable: false,
    minPopulation: 6,
    effects: [
      { kind: 'population', amount: -1 },
      { kind: 'grantSeconds', resource: 'insight', seconds: 400 },
    ],
  },
  {
    id: 'idleHands',
    title: 'A Quarrel Over Nothing',
    text: 'It starts over a share of roots and ends with two people not speaking. The work slows while it settles.',
    minAge: 0,
    weight: 6,
    favourable: false,
    minPopulation: 5,
    effects: [{ kind: 'buff', amount: -0.12, durationSeconds: 120 }],
  },

  // --- Age II --------------------------------------------------------------
  {
    id: 'lightningStrike',
    title: 'The Sky Gives Fire',
    text: 'A tree splits and burns on the ridge. The tribe carries a branch of it home at a run, in relay, all night.',
    minAge: 1,
    weight: 9,
    favourable: true,
    effects: [{ kind: 'grantSeconds', resource: 'ember', seconds: 240 }],
  },
  {
    id: 'perfectBurn',
    title: 'A Perfect Burn',
    text: 'The pit is opened and every piece in it is black the whole way through. Nobody is quite sure what was done differently.',
    minAge: 1,
    weight: 8,
    favourable: true,
    effects: [{ kind: 'grantSeconds', resource: 'charcoal', seconds: 220 }],
  },
  {
    id: 'fireGoesOut',
    title: 'The Fire Goes Out',
    text: 'Rain, and a firekeeper who slept. It is relit by noon and nobody says anything, which is worse.',
    minAge: 1,
    weight: 7,
    favourable: false,
    effects: [{ kind: 'buff', amount: -0.18, durationSeconds: 150 }],
  },
  {
    id: 'cookedWell',
    title: 'Something New, Cooked',
    text: 'A root that has always been poison turns out to be food if it is buried in coals for a day.',
    minAge: 1,
    weight: 7,
    favourable: true,
    effects: [
      { kind: 'grantSeconds', resource: 'forage', seconds: 250 },
      { kind: 'grantSeconds', resource: 'insight', seconds: 300 },
    ],
  },

  // --- Age III -------------------------------------------------------------
  {
    id: 'greatHerd',
    title: 'The Herd Comes Through',
    text: 'They cross the valley for two days without stopping. The tribe eats standing up and does not sleep.',
    minAge: 2,
    weight: 10,
    favourable: true,
    effects: [
      { kind: 'grantSeconds', resource: 'meat', seconds: 300 },
      { kind: 'grantSeconds', resource: 'hide', seconds: 300 },
    ],
  },
  {
    id: 'huntGoesWrong',
    title: 'The Hunt Goes Wrong',
    text: 'The animal turns. Everyone comes back, but two come back carried, and neither works for a while.',
    minAge: 2,
    weight: 7,
    favourable: false,
    minPopulation: 8,
    effects: [{ kind: 'buff', amount: -0.2, durationSeconds: 180 }],
  },
  {
    id: 'boneField',
    title: 'An Old Kill Site',
    text: 'Something died here a long time ago and something else stripped it. What is left is dry, clean and useful.',
    minAge: 2,
    weight: 8,
    favourable: true,
    effects: [{ kind: 'grantSeconds', resource: 'bone', seconds: 280 }],
  },
  {
    id: 'firstChild',
    title: 'A Winter Survived',
    text: 'Every one of them is still here in spring. The tribe has never managed that before.',
    minAge: 2,
    weight: 6,
    favourable: true,
    minPopulation: 10,
    effects: [
      { kind: 'population', amount: 3 },
      { kind: 'buff', amount: 0.25, durationSeconds: 240 },
    ],
  },

  // --- Age IV --------------------------------------------------------------
  {
    id: 'standingGrove',
    title: 'A Standing Grove',
    text: 'Straight timber, close together, an easy drag downhill to camp. It is almost suspiciously convenient.',
    minAge: 3,
    weight: 9,
    favourable: true,
    effects: [{ kind: 'grantSeconds', resource: 'timber', seconds: 300 }],
  },
  {
    id: 'stormNight',
    title: 'A Night of Storm',
    text: 'The roof holds. That is the whole story, and the tribe tells it for years.',
    minAge: 3,
    weight: 8,
    favourable: true,
    effects: [{ kind: 'buff', amount: 0.35, durationSeconds: 240 }],
  },
  {
    id: 'roofFalls',
    title: 'A Roof Comes Down',
    text: 'Too much thatch, too little cordage, and a beam that everyone had privately worried about.',
    minAge: 3,
    weight: 6,
    favourable: false,
    effects: [{ kind: 'buff', amount: -0.2, durationSeconds: 200 }],
  },

  // --- Age V ---------------------------------------------------------------
  {
    id: 'deepCave',
    title: 'The Deep Cave',
    text: 'A crawl, then a chamber. Someone lifts a light and the whole tribe stops talking at once.',
    minAge: 4,
    weight: 9,
    favourable: true,
    effects: [
      { kind: 'grantSeconds', resource: 'insight', seconds: 500 },
      { kind: 'grantSeconds', resource: 'ochre', seconds: 250 },
    ],
  },
  {
    id: 'theOtherTribe',
    title: 'Marks Not Ours',
    text: 'On a wall three days east, hands in ochre. Smaller than the tribe’s, and older, and unmistakably the same idea.',
    minAge: 4,
    weight: 7,
    favourable: true,
    effects: [
      { kind: 'grantSeconds', resource: 'insight', seconds: 700 },
      { kind: 'buff', amount: 0.3, durationSeconds: 300 },
    ],
  },

  // --- Age VI --------------------------------------------------------------
  {
    id: 'firstHarvest',
    title: 'It Came Back',
    text: 'What was put in the ground last year is standing in it this year, in rows, where it was put. Nobody speaks for a while.',
    minAge: 5,
    weight: 10,
    favourable: true,
    effects: [
      { kind: 'grantSeconds', resource: 'grain', seconds: 400 },
      { kind: 'buff', amount: 0.4, durationSeconds: 300 },
    ],
  },
  {
    id: 'potSurvives',
    title: 'The Pot That Held',
    text: 'Grain sealed in fired clay, opened after a winter, and every seed in it still good.',
    minAge: 5,
    weight: 8,
    favourable: true,
    effects: [{ kind: 'grantSeconds', resource: 'pottery', seconds: 350 }],
  },
  {
    id: 'blight',
    title: 'A Bad Year',
    text: 'The rain does not come when it always comes. The tribe learns that a field can fail in ways a forest never did.',
    minAge: 5,
    weight: 6,
    favourable: false,
    effects: [{ kind: 'buff', amount: -0.22, durationSeconds: 240 }],
  },

  // --- Any age -------------------------------------------------------------
  {
    id: 'quietWork',
    title: 'Nothing Worth Telling',
    text: 'The days pass. The work is done. Later nobody can remember a single thing about this stretch of time, which is its own kind of luck.',
    minAge: 0,
    weight: 5,
    favourable: true,
    effects: [{ kind: 'vigor', stacks: 5 }],
  },
  {
    id: 'someoneRemembers',
    title: 'Someone Remembers',
    text: 'An argument about how a thing was done before is settled by the oldest person present, who turns out to be right.',
    minAge: 0,
    weight: 6,
    favourable: true,
    effects: [{ kind: 'grantSeconds', resource: 'insight', seconds: 350 }],
  },
];

export function talesForAge(ageIndex: number): TaleDef[] {
  return TALES.filter(
    (tale) => ageIndex >= tale.minAge && (tale.maxAge === undefined || ageIndex <= tale.maxAge),
  );
}
